import { last } from 'lodash';

import { assertValue } from '@libs/utils';

import type { Marker } from '../maps';

import { ConnectionType } from './types';
import type {
  Route,
  RouteConnection,
  RouteSegmentBoundaries,
  RouteSegmentByConnectionType,
  RouteWithDetails,
} from './types';

export function getConnectionType(marker: Marker): ConnectionType {
  if (!marker.data || typeof marker.data === 'string') {
    return ConnectionType.NONE;
  }

  return marker.data.connectionToBeReached ?? ConnectionType.NONE;
}

export function getRouteSegmentsByConnectionType(
  route: Route
): RouteSegmentByConnectionType[] {
  return route.reduce(
    (
      routeSegmentsByConnectionType: RouteSegmentByConnectionType[],
      currentRouteConnection: RouteConnection,
      index
    ) => {
      if (index === 0) {
        routeSegmentsByConnectionType.push({
          segment: [currentRouteConnection],
          type: currentRouteConnection.type,
        });
      } else {
        const previousRouteConnection = route[index - 1];
        assertValue(
          previousRouteConnection,
          'helpers.ts > getRouteSegmentsByConnectionType > previousRouteConnection'
        );

        if (
          previousRouteConnection.type === currentRouteConnection.type &&
          previousRouteConnection.to.key === currentRouteConnection.from.key
        ) {
          const lastRouteSegment = last(routeSegmentsByConnectionType);

          lastRouteSegment?.segment.push(currentRouteConnection);
        } else {
          routeSegmentsByConnectionType.push({
            segment: [currentRouteConnection],
            type: currentRouteConnection.type,
          });
        }
      }

      return routeSegmentsByConnectionType;
    },
    []
  );
}

export function getRouteSegmentBoundaries(
  routeSegment: RouteSegmentByConnectionType
): RouteSegmentBoundaries {
  const from = routeSegment.segment[0]?.from;
  const to = routeSegment.segment[routeSegment.segment.length - 1]?.to;

  if (!from || !to) {
    throw new Error('Trying to get boundaries from empty segment.');
  }

  const waypoints = getWaypointsFromSegment(routeSegment.segment);

  return {
    from,
    to,
    waypoints,
    type: routeSegment.type,
  };
}

function getWaypointsFromSegment(segment: Route): Marker[] {
  return segment.slice(0, -1).map((connection) => connection.to);
}

export function isRoutableConnectionType(
  connectionType: ConnectionType
): boolean {
  return [
    ConnectionType.CAR,
    ConnectionType.BICYCLE,
    ConnectionType.WALK,
  ].includes(connectionType);
}

export function getDirectionsRequest(
  segmentBoundaries: RouteSegmentBoundaries
): google.maps.DirectionsRequest {
  const origin = new google.maps.LatLng(
    segmentBoundaries.from.position.latitude,
    segmentBoundaries.from.position.longitude
  );
  const destination = new google.maps.LatLng(
    segmentBoundaries.to.position.latitude,
    segmentBoundaries.to.position.longitude
  );

  const googleWaypoints = segmentBoundaries.waypoints.map((waypoint) => ({
    location: new google.maps.LatLng(
      waypoint.position.latitude,
      waypoint.position.longitude
    ),
    stopover: true,
  }));

  return {
    origin,
    destination,
    waypoints: googleWaypoints,
    travelMode: getGoogleMapsTravelMode(segmentBoundaries.type),
  };
}

function getGoogleMapsTravelMode(
  connectionType: ConnectionType
): google.maps.TravelMode {
  switch (connectionType) {
    case ConnectionType.CAR:
      return google.maps.TravelMode.DRIVING;

    case ConnectionType.WALK:
      return google.maps.TravelMode.WALKING;

    case ConnectionType.BICYCLE:
      return google.maps.TravelMode.BICYCLING;

    //TODO: Change custom ConnectionTypes withouth using google.maps.TravelMode
    case ConnectionType.NONE:
      return google.maps.TravelMode.WALKING;

    case ConnectionType.FLIGHT:
      return google.maps.TravelMode.DRIVING;

    case ConnectionType.FERRY:
      return google.maps.TravelMode.DRIVING;

    default:
      return google.maps.TravelMode.DRIVING;
  }
}

export function getRouteWithDetailsFromDirectionsResult(
  result: google.maps.DirectionsResult,
  segmentBoundaries: RouteSegmentBoundaries
): RouteWithDetails {
  const allSegmentMarkers = [
    segmentBoundaries.from,
    ...segmentBoundaries.waypoints,
    segmentBoundaries.to,
  ];
  const route = result.routes[0];
  assertValue(
    route,
    'helpers.ts > getRouteWithDetailsFromDirectionsResult > route '
  );

  return route.legs.map((leg, index) => {
    const distanceInMeters = leg.distance?.value;
    const timeInSeconds = leg.duration?.value;
    const secondsInMinute = 60;
    const metersInKilometer = 1000;

    const distanceInKilometers = distanceInMeters
      ? distanceInMeters / metersInKilometer
      : undefined;
    const expectedTravelTimeInMinutes = timeInSeconds
      ? timeInSeconds / secondsInMinute
      : undefined;

    const fromMarker = allSegmentMarkers[index];
    const toMarker = allSegmentMarkers[index + 1];
    assertValue(
      fromMarker,
      'helpers.ts > getRouteWithDetailsFromDirectionsResult > fromMarker'
    );
    assertValue(
      toMarker,
      'helpers.ts > getRouteWithDetailsFromDirectionsResult > toMarker'
    );
    const connection = segmentBoundaries.type;

    return {
      distanceInKilometers,
      expectedTravelTimeInMinutes,
      type: connection,
      from: { ...fromMarker },
      to: { ...toMarker },
    };
  });
}
